import React, { useState, useRef, useEffect } from "react";
import usePDA1091_R01Store from "./PDA1091_R01Store";
import { usePDA1091_R01Content } from "./PDA1091_R01Content";
import useUiuxStore from "src/store/useUiuxStore";
import { WareHouseDto } from "src/dtos/warehouse/wareHouseDto";

/**
 * PDA1091_R01 품목실사 입력 폼
 * PDA1090_R01 디자인 스타일 적용
 */
const PDA1091_R01Info = () => {
  const {
    warehouseCd,
    setWarehouseCd,
    warehouseNm,
    setWarehouseNm,
    scanValue,
    setScanValue,
    scannedItem,
    actualStock,
    setActualStock,
    loading,
    auditItems,
  } = usePDA1091_R01Store();

  const scanInputRef = useRef<HTMLInputElement>(null);
  const actualStockInputRef = useRef<HTMLInputElement>(null);
  const { handleScanItem, handleAddToGrid, handleSaveAll, handleCancel } =
    usePDA1091_R01Content(scanInputRef);

  const { setCodeSearchModalVisible, setCodeSearchModalProps } = useUiuxStore();
  const warehouseLocked = loading || auditItems.length > 0;

  const [keyboardEnabled, setKeyboardEnabled] = useState(false);
  const [lastTapTime, setLastTapTime] = useState(0);

  useEffect(() => {
    scanInputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (scannedItem) {
      actualStockInputRef.current?.focus();
    }
  }, [scannedItem]);

  const handleDoubleTap = () => {
    const now = Date.now();
    const timeDiff = now - lastTapTime;

    if (timeDiff < 300) {
      setKeyboardEnabled(true);
      scanInputRef.current?.focus();
    }

    setLastTapTime(now);
  };

  const toggleKeyboard = () => {
    setKeyboardEnabled(!keyboardEnabled);
    if (!keyboardEnabled) {
      setTimeout(() => scanInputRef.current?.focus(), 100);
    }
  };

  const handleScanKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleScanItem();
    }
  };

  const handleActualStockKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddToGrid();
    }
  };

  const handleActualStockChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "") {
      setActualStock(null);
    } else {
      setActualStock(Number(value));
    }
  };

  const openWarehouseSearchModal = () => {
    if (warehouseLocked) return;

    setCodeSearchModalProps({
      category: "창고코드",
      search: "",
      autoSelectSingle: true,
      autoFocus: false,
      onConfirm: (selectedWarehouse: WareHouseDto) => {
        setWarehouseCd(selectedWarehouse.warehouseCd);
        setWarehouseNm(selectedWarehouse.warehouseNm);
        setCodeSearchModalVisible(false);
        setTimeout(() => scanInputRef.current?.focus(), 100);
      },
      onCloseConfirm: () => {
        setCodeSearchModalVisible(false);
      },
    });
    setCodeSearchModalVisible(true);
  };

  const handleWarehouseKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      openWarehouseSearchModal();
    }
  };

  const getDiffDisplay = () => {
    if (!scannedItem || actualStock === null) return "";
    const diff = actualStock - scannedItem.currentStock;
    if (diff === 0) return "";
    const sign = diff > 0 ? "+" : "";
    return `(${sign}${diff.toLocaleString()})`;
  };

  return (
    <div className="w-full bg-white p-4 pb-2 border-b border-gray-200">
      <div className="space-y-1">

        {/* Scan (품목/바코드) */}
        <div className="flex items-center gap-1">
          <label className="text-lg font-medium text-black w-20 flex-shrink-0">Scan</label>
          <div className="flex-1 flex gap-1">
            <input
              ref={scanInputRef}
              type="text"
              value={scanValue}
              onChange={(e) => setScanValue(e.target.value)}
              onKeyDown={handleScanKeyDown}
              onClick={handleDoubleTap}
              inputMode={keyboardEnabled ? "text" : "none"}
              disabled={loading}
              placeholder="품목/바코드 스캔"
              autoComplete="off"
              className="flex-1 max-w-[173px] px-2 text-lg border border-gray-300 rounded bg-white text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={toggleKeyboard}
              className={`w-10 flex-shrink-0 px-2 border border-gray-300 rounded transition-colors ${
                keyboardEnabled
                  ? "bg-blue-500 text-white"
                  : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
              title={keyboardEnabled ? "키보드 숨기기" : "키보드 표시"}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* 품목코드 */}
        <div className="flex items-center gap-1">
          <label className="text-lg font-medium text-black w-20 flex-shrink-0">품목코드</label>
          <div className="flex-1 flex gap-1">
            <input
              type="text"
              value={scannedItem?.itemCd || ""}
              readOnly
              className="flex-1 max-w-[173px] px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
            />
            <div className="w-10 flex-shrink-0"></div>
          </div>
        </div>

        {/* 품목명 */}
        <div className="flex items-center gap-1">
          <label className="text-lg font-medium text-black w-20 flex-shrink-0">품목</label>
          <div className="flex-1 flex gap-1">
            <input
              type="text"
              value={scannedItem?.itemNm || ""}
              readOnly
              className="flex-1 max-w-[173px] px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
            />
            <div className="w-10 flex-shrink-0"></div>
          </div>
        </div>

        {/* 규격 */}
        <div className="flex items-center gap-1">
          <label className="text-lg font-medium text-black w-20 flex-shrink-0">규격</label>
          <div className="flex-1 flex gap-1">
            <input
              type="text"
              value={scannedItem?.itemSpec || ""}
              readOnly
              className="flex-1 max-w-[173px] px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
            />
            <div className="w-10 flex-shrink-0"></div>
          </div>
        </div>

        {/* 단위 및 등급 */}
        <div className="flex items-center gap-1">
          <div className="flex items-center gap-1">
            <label className="text-lg font-medium text-black w-20 flex-shrink-0">단위</label>
            <input
              type="text"
              value={scannedItem?.itemUnit || ""}
              readOnly
              className="w-[50px] px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
            />
          </div>
          <div className="flex items-center gap-1">
            <label className="text-lg font-medium text-black w-10 flex-shrink-0">등급</label>
            <input
              type="text"
              value={scannedItem?.itemStatus || ""}
              readOnly
              className="w-[50px] px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
            />
          </div>
        </div>

        {/* 창고 */}
        <div className="flex items-center gap-1">
          <label className="text-lg font-medium text-black w-20 flex-shrink-0">창고</label>
          <div className="flex-1 flex gap-1">
            <input
              type="text"
              value={warehouseNm}
              onClick={openWarehouseSearchModal}
              onKeyDown={handleWarehouseKeyDown}
              readOnly
              autoComplete="off"
              placeholder="창고 선택"
              disabled={warehouseLocked}
              className="flex-1 max-w-[173px] px-2 text-lg border border-gray-300 rounded bg-white text-black focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
            <button
              type="button"
              onClick={openWarehouseSearchModal}
              disabled={warehouseLocked}
              className="w-10 px-2 border border-gray-300 rounded bg-blue-500 text-white hover:bg-blue-600 transition-colors flex-shrink-0 disabled:bg-gray-300 disabled:cursor-not-allowed"
              title="창고 검색"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* 현재고 */}
        <div className="flex items-center gap-1">
          <label className="text-lg font-medium text-black w-20 flex-shrink-0">현재고</label>
          <div className="flex-1 flex gap-1">
            <input
              type="text"
              value={scannedItem ? scannedItem.currentStock.toLocaleString() : ""}
              readOnly
              className="flex-1 max-w-[173px] px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
            />
            <div className="w-10 flex-shrink-0"></div>
          </div>
        </div>

        {/* 실재고 및 검수 버튼 */}
        <div className="flex items-center gap-1">
          <label className="text-lg font-medium text-black w-20 flex-shrink-0">실재고</label>
          <div className="flex-1 flex gap-1 items-center">
            <input
              ref={actualStockInputRef}
              type="number"
              value={actualStock !== null ? actualStock : ""}
              onChange={handleActualStockChange}
              onKeyDown={handleActualStockKeyDown}
              disabled={loading || !scannedItem}
              placeholder="수량"
              className="w-24 px-2 text-lg border border-gray-300 rounded bg-white text-black focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              min="0"
              step="1"
            />
            <span
              className={`text-lg font-medium ${
                actualStock !== null && scannedItem && actualStock !== scannedItem.currentStock
                  ? actualStock > scannedItem.currentStock
                    ? "text-blue-600"
                    : "text-red-600"
                  : "text-gray-400"
              }`}
            >
              {getDiffDisplay()}
            </span>
            <button
              type="button"
              onClick={handleAddToGrid}
              disabled={loading || !scannedItem || actualStock === null}
              className="flex-1 bg-blue-500 text-white px-2 rounded text-lg font-medium hover:bg-blue-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              검수 {auditItems.length > 0 && `(${auditItems.length})`}
            </button>
          </div>
        </div>

        {/* 취소 / 실사등록 */}
        <div className="flex gap-1 pt-2">
          <button
            type="button"
            onClick={handleCancel}
            className="flex-1 py-1 text-lg bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            취소
          </button>
          <button
            type="button"
            onClick={handleSaveAll}
            disabled={loading || auditItems.length === 0}
            className="flex-1 py-1 text-lg bg-green-500 text-white rounded hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {auditItems.length > 0 ? `실사등록(${auditItems.length})` : "실사등록"}
          </button>
        </div>

      </div>
    </div>
  );
};

export default PDA1091_R01Info;
